import React, { useState, useEffect } from 'react';
import { highscoresService, HighscoreEntry } from '../services/firebaseService';
import './Highscores.css';

const Highscores: React.FC = () => {
    const [highscores, setHighscores] = useState<HighscoreEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingEntry, setEditingEntry] = useState<HighscoreEntry | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newEntry, setNewEntry] = useState({ userId: '', name: '', level: 1 });
    const [isRebuilding, setIsRebuilding] = useState(false);

    useEffect(() => {
        fetchHighscores();
    }, []);

    const fetchHighscores = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await highscoresService.getHighscores();
            // Sort by level descending
            const sorted = data.sort((a, b) => b.level - a.level);
            setHighscores(sorted);
        } catch (err) {
            setError('Failed to load highscores');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (entry: HighscoreEntry) => {
        setEditingEntry({ ...entry });
    };

    const handleSaveEdit = async () => {
        if (!editingEntry) return;

        try {
            await highscoresService.updateHighscore(editingEntry.userId, {
                name: editingEntry.name,
                level: editingEntry.level
            });
            await fetchHighscores();
            setEditingEntry(null);
        } catch (err) {
            alert('Failed to update highscore');
            console.error(err);
        }
    };

    const handleDelete = async (userId: string, name: string) => {
        if (!window.confirm(`Are you sure you want to delete ${name}'s highscore?`)) return;

        try {
            await highscoresService.deleteHighscore(userId);
            await fetchHighscores();
        } catch (err) {
            alert('Failed to delete highscore');
            console.error(err);
        }
    };

    const handleCreate = async () => {
        if (!newEntry.userId || !newEntry.name) {
            alert('Please fill in all fields');
            return;
        }

        try {
            await highscoresService.createHighscore(newEntry.userId, {
                userId: newEntry.userId,
                name: newEntry.name,
                level: newEntry.level
            });
            await fetchHighscores();
            setShowCreateModal(false);
            setNewEntry({ userId: '', name: '', level: 1 });
        } catch (err) {
            alert('Failed to create highscore');
            console.error(err);
        }
    };

    const handleViewUser = (userId: string) => {
        // Navigate to users tab with the selected user for editing
        window.location.href = `/users?edit=${userId}`;
    };

    const handleRebuild = async () => {
        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm(
            'Are you sure you want to rebuild the highscores from scratch?\n\n' +
            'This will:\n' +
            '1. Clear all current highscores\n' +
            '2. Scan all users in the Users collection\n' +
            '3. Select the top 10 users by level\n\n' +
            'This action cannot be undone.'
        );

        if (!confirmed) return;

        setIsRebuilding(true);
        try {
            await highscoresService.rebuildHighscores();
            await fetchHighscores(); // Reload to show new data
        } catch (err) {
            alert('Failed to rebuild highscores');
            console.error('Rebuild error:', err);
        } finally {
            setIsRebuilding(false);
        }
    };

    if (loading) {
        return <div className="highscores-container"><p>Loading highscores...</p></div>;
    }

    if (error) {
        return <div className="highscores-container"><p className="error">{error}</p></div>;
    }

    return (
        <div className="highscores-container">
            <div className="highscores-header">
                <h1>Highscores Management</h1>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                        className="btn-create"
                        onClick={handleRebuild}
                        disabled={isRebuilding}
                        style={{
                            background: '#3b82f6',
                            opacity: isRebuilding ? 0.6 : 1,
                            cursor: isRebuilding ? 'not-allowed' : 'pointer'
                        }}
                    >
                        🔄 {isRebuilding ? 'Rebuilding...' : 'Rebuild from Users'}
                    </button>
                    <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                        + Create Entry
                    </button>
                </div>
            </div>

            <div className="highscores-stats">
                <div className="stat-card">
                    <span className="stat-label">Total Entries</span>
                    <span className="stat-value">{highscores.length}</span>
                </div>
                <div className="stat-card">
                    <span className="stat-label">Highest Level</span>
                    <span className="stat-value">{highscores[0]?.level || 0}</span>
                </div>
            </div>

            <div className="highscores-table-container">
                <table className="highscores-table">
                    <thead>
                        <tr>
                            <th>Rank</th>
                            <th>Name</th>
                            <th>Level</th>
                            <th>User ID</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {highscores.map((entry, index) => (
                            <tr key={entry.userId} className={index < 3 ? `top-${index + 1}` : ''}>
                                <td className="rank-cell">
                                    {index < 3 ? (
                                        <span className="rank-badge">{index + 1}</span>
                                    ) : (
                                        <span className="rank-number">{index + 1}</span>
                                    )}
                                </td>
                                <td className="name-cell">{entry.name}</td>
                                <td className="level-cell">
                                    <span className="level-badge">{entry.level}</span>
                                </td>
                                <td className="userid-cell">
                                    <button
                                        className="btn-link"
                                        onClick={() => handleViewUser(entry.userId)}
                                        title="View user profile"
                                    >
                                        {entry.userId}
                                    </button>
                                </td>
                                <td className="actions-cell">
                                    <button
                                        className="btn-edit"
                                        onClick={() => handleEdit(entry)}
                                        title="Edit entry"
                                    >
                                        ✏️ Edit
                                    </button>
                                    <button
                                        className="btn-delete"
                                        onClick={() => handleDelete(entry.userId, entry.name)}
                                        title="Delete entry"
                                    >
                                        🗑️ Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {highscores.length === 0 && (
                    <div className="empty-state">
                        <p>No highscore entries found.</p>
                        <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                            Create First Entry
                        </button>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editingEntry && (
                <div className="modal-overlay" onClick={() => setEditingEntry(null)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Edit Highscore</h2>
                        <div className="form-group">
                            <label>User ID (Read-only)</label>
                            <input type="text" value={editingEntry.userId} disabled />
                        </div>
                        <div className="form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                value={editingEntry.name}
                                onChange={(e) => setEditingEntry({ ...editingEntry, name: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label>Level</label>
                            <input
                                type="number"
                                value={editingEntry.level}
                                min="1"
                                onChange={(e) => setEditingEntry({ ...editingEntry, level: parseInt(e.target.value) || 1 })}
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setEditingEntry(null)}>Cancel</button>
                            <button className="btn-save" onClick={handleSaveEdit}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>Create Highscore Entry</h2>
                        <div className="form-group">
                            <label>User ID *</label>
                            <input
                                type="text"
                                value={newEntry.userId}
                                onChange={(e) => setNewEntry({ ...newEntry, userId: e.target.value })}
                                placeholder="e.g., user123"
                            />
                        </div>
                        <div className="form-group">
                            <label>Name *</label>
                            <input
                                type="text"
                                value={newEntry.name}
                                onChange={(e) => setNewEntry({ ...newEntry, name: e.target.value })}
                                placeholder="e.g., אלי"
                            />
                        </div>
                        <div className="form-group">
                            <label>Level *</label>
                            <input
                                type="number"
                                value={newEntry.level}
                                min="1"
                                onChange={(e) => setNewEntry({ ...newEntry, level: parseInt(e.target.value) || 1 })}
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
                            <button className="btn-save" onClick={handleCreate}>Create Entry</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Highscores;
